import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"
import InfoIcon from "@mui/icons-material/Info"
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges"
import {
	Box,
	Card,
	CardActions,
	CardContent,
	CircularProgress,
	Grid,
	IconButton,
	Theme,
	Tooltip,
	Typography,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { format, parseISO } from "date-fns"
import { toZonedTime } from "date-fns-tz"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"

import { AppDispatch } from "../../../store"
import { sendOtpToApprove } from "../../../store/actions/approve"
import { BankEssential } from "../../../store/actions/bank"
import { CorporateEssential } from "../../../store/actions/corporates"
import { FileState } from "../../../store/actions/file"
import { RootState } from "../../../store/reducers"
import { getBestContrastColor } from "../../../utils/contrast-color"
import ModalOtp from "../../modal-otp"
import Status from "../../status"

const FileCard = ({
	fileInfo,
	userTimeZone,
}: {
	fileInfo: FileState
	userTimeZone: any
}) => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const dispatch = useDispatch<AppDispatch>()

	const userPermissions = useSelector(
		(state: RootState) => state.auth?.user?.permissions
	)
	const currentUsername = useSelector(
		(state: RootState) => state.auth?.user?.username
	)

	const hasViewTransactionEnabled: boolean = userPermissions
		? userPermissions.some(
				(perm) =>
					perm.permissionName === "VIEW_TRANSACTION" && perm.enabled === true
			)
		: false

	const hasCrudTransactionEnabled: boolean = userPermissions
		? userPermissions.some(
				(perm) =>
					perm.permissionName === "CRUD_TRANSACTION" && perm.enabled === true
			)
		: false

	const hasAuthorizeEnabled: boolean = userPermissions
		? userPermissions.some(
				(perm) => perm.permissionName === "AUTHORIZE" && perm.enabled === true
			)
		: false

	const alreadyAuthorizedByCurrentUser =
		fileInfo.status === "INTERMEDIATE_APPROVED" &&
		Boolean(currentUsername) &&
		fileInfo.lastAuthorizedBy === currentUsername

	const canAuthorizeFile =
		hasAuthorizeEnabled &&
		!alreadyAuthorizedByCurrentUser &&
		(fileInfo.status === "DRAFT" ||
			fileInfo.status === "INTERMEDIATE_APPROVED" ||
			fileInfo.status === "ERROR")

	const banksList: BankEssential[] = useSelector(
		(state: RootState) => state.banksUser?.list
	)
	const corporateUserList: CorporateEssential[] | undefined = useSelector(
		(state: RootState) => state?.corporatesUser?.list
	)
	const [bankName, setBankName] = useState<string | undefined>(undefined)
	const [corporateName, setCorporateName] = useState<string | undefined>(
		undefined
	)
	const [openOtpDrawer, setOpenOtpDrawer] = useState<boolean>(false)
	const [isAuthorized, setIsAuthorized] = useState<boolean | undefined>(
		undefined
	)
	const fileDate = parseISO(fileInfo.creationDate)
	const zonedDate = toZonedTime(fileDate, userTimeZone)
	const formattedDate = format(zonedDate, "dd/MM/yyyy")
	const formattedDateTime = format(zonedDate, "dd/MM/yyyy HH:mm:ss")

	useEffect(() => {
		if (banksList && fileInfo.idBank) {
			const currentBankName: string | undefined = banksList.find(
				(bank: BankEssential) => bank.id === fileInfo.idBank
			)?.name
			setBankName(currentBankName || fileInfo.idBank)
		}
	}, [banksList])

	useEffect(() => {
		if (banksList && fileInfo.corporateSenderId) {
			const currentCorporateName: string | undefined = corporateUserList.find(
				(corporate: CorporateEssential) =>
					corporate.id === fileInfo.corporateSenderId
			)?.name
			setCorporateName(currentCorporateName || fileInfo.corporateSenderId)
		}
	}, [banksList])

	const PaydoCard = styled(Card)(({ theme }: { theme: Theme }) => ({
		backgroundColor: getBestContrastColor(theme.palette.primary.main),
		width: "300px",
		margin: "16px",
		boxShadow:
			"0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
	}))

	const CardHeader = styled(Box)(({ theme }: { theme: Theme }) => ({
		display: "flex",
		justifyContent: "space-between",
		color: theme.palette.primary.main,
		marginBottom: "4px",
		alignItems: "flex-start",
		[`& .title`]: {
			color: theme.palette.primary.main,
		},
	}))

	const CardInfo = styled(Typography)(() => ({
		color: "#555",
		marginBottom: "8px",
	}))

	const CardHighlightedInfo = styled(Box)(({ theme }: { theme: Theme }) => ({
		display: "flex",
		justifyContent: "space-between",
		color: theme.palette.primary.main,
		alignItems: "center",
	}))

	const CardFooter = styled(CardActions)(() => ({
		backgroundColor: "#F0F0F0",
		padding: "8px",
		borderRadius: "0 0 8px 8px",
		display: "flex",
		justifyContent: "space-between",
	}))

	const GridContainer = styled(Grid)(() => ({
		display: "flex",
		alignItems: "space-beetween",
		width: "100%",
	}))

	const TextEllipsis = styled(Grid)(() => ({
		overflow: "hidden",
		whiteSpace: "nowrap",
		textOverflow: "ellipsis",
		flexGrow: 1,
		marginRight: 8,
	}))

	const GridDate = styled(Grid)(() => ({
		maxWidth: "85px",
		textAlign: "right",
	}))

	const requestOtpForApprove = (isAuthorized: boolean) => {
		setIsAuthorized(isAuthorized)
		dispatch(
			sendOtpToApprove({
				bankId: fileInfo.idBank,
				corporateId: fileInfo.corporateSenderId,
				fileId: fileInfo.id.toString(),
			})
		)
			.unwrap()
			.then((result: boolean) => {
				setOpenOtpDrawer(result ?? false)
			})
			.catch(() => {
				setOpenOtpDrawer(false)
			})
	}

	return (
		<>
			<PaydoCard
				sx={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "space-between",
					border: fileInfo.correctFile ? 0 : "2px solid rgb(211, 47, 47)",
				}}
			>
				<CardContent>
					<CardHeader>
						<GridContainer container wrap="nowrap">
							<Grid item xs zeroMinWidth>
								<CardInfo className={`title `} variant="body1">
									<Tooltip title={fileInfo.originalFileName}>
										<TextEllipsis>{fileInfo.originalFileName}</TextEllipsis>
									</Tooltip>{" "}
									<div style={{ marginTop: "-4px" }}>
										<CardInfo variant="caption">
											{t("card.plicksNumber", { ns: "distinte" })}:{" "}
											{fileInfo.plicksCount}
										</CardInfo>
									</div>
								</CardInfo>
							</Grid>
							<GridDate item>
								<Typography
									component="div"
									variant="subtitle2"
									color="textSecondary"
								>
									<Tooltip title={formattedDateTime}>
										<span>{formattedDate}</span>
									</Tooltip>
								</Typography>
							</GridDate>
						</GridContainer>
					</CardHeader>
					<CardInfo variant="body2">
						<b>{t("card.bank", { ns: "distinte" })}:</b> {bankName}
					</CardInfo>
					<CardInfo variant="body2">
						<b>{t("card.corporate", { ns: "distinte" })}</b>: {corporateName}
					</CardInfo>
					<CardInfo variant="body2">
						<b>{t("card.campaign", { ns: "distinte" })}</b>:{" "}
						{fileInfo.campaignName}
					</CardInfo>

					<CardHighlightedInfo>
						<Status status={fileInfo.status} />
						<CardInfo sx={{ textAlign: "right" }}>
							{" "}
							<Typography variant="caption">
								<b>{t("card.total", { ns: "distinte" })}</b>
							</Typography>
							<br />€ {fileInfo.totalAmount}{" "}
						</CardInfo>
					</CardHighlightedInfo>
				</CardContent>
				<CardFooter>
					<Box sx={{ display: "flex", gap: 1 }}>
						{hasViewTransactionEnabled && (
							<IconButton
								color="primary"
								onClick={() =>
									navigate(
										`/distinta/${fileInfo?.id}?filename=${encodeURIComponent(fileInfo.originalFileName)}`,
										{ state: { filename: fileInfo.originalFileName } }
									)
								}
							>
								<Tooltip title={t("tooltips.viewDetail", { ns: "distinte" })}>
									<InfoIcon />
								</Tooltip>
							</IconButton>
						)}
						{!fileInfo.correctFile && hasCrudTransactionEnabled && (
							<IconButton
								color="primary"
								onClick={() =>
									navigate(
										`/upload/${fileInfo?.id}?filename=${encodeURIComponent(fileInfo.originalFileName)}`,
										{ state: { filename: fileInfo.originalFileName } }
									)
								}
							>
								<Tooltip title={t("tooltips.reupload", { ns: "distinte" })}>
									<PublishedWithChangesIcon />
								</Tooltip>
							</IconButton>
						)}
					</Box>
					{canAuthorizeFile && (
						<Box sx={{ display: "flex", gap: 1 }}>
							{openOtpDrawer ? (
								<CircularProgress size={32} sx={{}} />
							) : (
								<>
									{fileInfo?.status !== "ERROR" && (
										<IconButton
											color="success"
											onClick={() => requestOtpForApprove(true)}
										>
											<Tooltip
												title={t("tooltips.approve", { ns: "distinte" })}
											>
												<CheckIcon />
											</Tooltip>
										</IconButton>
									)}
									<IconButton
										color="error"
										onClick={() => requestOtpForApprove(false)}
									>
										<Tooltip title={t("tooltips.refuses", { ns: "distinte" })}>
											<CloseIcon />
										</Tooltip>
									</IconButton>
								</>
							)}
						</Box>
					)}
				</CardFooter>
			</PaydoCard>
			<ModalOtp
				fileInfo={fileInfo}
				isAuthorized={isAuthorized}
				openOtpDrawer={openOtpDrawer}
				setOpenOtpDrawer={setOpenOtpDrawer}
			/>
		</>
	)
}

export default FileCard
